import { useEffect, useMemo, useState } from 'react';
import { DndContext, closestCenter, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCardsStore } from '../stores/cards';
import { useCircleStore } from '../stores/circle';
import { useAuthStore } from '../stores/auth';
import { CATEGORIES, type CardTemplate } from '@nudge/shared';
import { CardTile } from '../components/cards/CardTile';
import { CardEditor } from '../components/cards/CardEditor';
import { CardActionsMenu } from '../components/cards/CardActionsMenu';
import { toast } from '../components/ui/Toast';
import { groupTemplatesByCategory } from '../utils/groupTemplatesByCategory';
import { formatError } from '../utils/formatError';

function SortableTile({ template, onLongPress }: { template: CardTemplate; onLongPress: (t: CardTemplate) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: template.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <CardTile template={template} onLongPress={() => onLongPress(template)} size="comfortable" />
    </div>
  );
}

export function CardsPage() {
  const circle = useCircleStore((s) => s.circle);
  const me = useAuthStore((s) => s.user);
  const templates = useCardsStore((s) => s.templates);
  const fetchDeck = useCardsStore((s) => s.fetchDeck);
  const createTemplate = useCardsStore((s) => s.createTemplate);
  const updateTemplate = useCardsStore((s) => s.updateTemplate);
  const archiveTemplate = useCardsStore((s) => s.archiveTemplate);
  const reorderTemplates = useCardsStore((s) => s.reorderTemplates);
  const toggleFavorite = useCardsStore((s) => s.toggleFavorite);

  const [editor, setEditor] = useState<{ open: boolean; initial?: CardTemplate }>({ open: false });
  const [actionsTarget, setActionsTarget] = useState<CardTemplate | null>(null);

  useEffect(() => {
    if (circle) void fetchDeck(circle.id);
  }, [circle, fetchDeck]);

  const grouped = useMemo(() => groupTemplatesByCategory(templates), [templates]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  function onDragEnd(category: string) {
    return async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const arr = grouped.get(category) ?? [];
      const oldIndex = arr.findIndex((t) => t.id === active.id);
      const newIndex = arr.findIndex((t) => t.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(arr, oldIndex, newIndex);
      const updates = next.map((t, i) => ({ id: t.id, sort_order: i * 10 }));
      try {
        await reorderTemplates(updates);
      } catch (e) {
        toast(formatError(e, 'Could not reorder cards'), 'error');
      }
    };
  }

  async function submitEditor(values: { icon: string; label: string; subtext: string; category: string; colour: string | null; is_shared: boolean }) {
    if (!circle) return;
    try {
      if (editor.initial) {
        await updateTemplate(editor.initial.id, {
          icon: values.icon,
          label: values.label,
          subtext: values.subtext || null,
          category: values.category,
          colour: values.colour,
          is_shared: values.is_shared,
        });
        toast('Card updated', 'success');
      } else {
        await createTemplate({
          circle_id: circle.id,
          icon: values.icon,
          label: values.label,
          subtext: values.subtext || undefined,
          category: values.category,
          colour: values.colour ?? undefined,
          is_shared: values.is_shared,
        });
        toast('Card created', 'success');
      }
    } catch (e) {
      toast(formatError(e, 'Could not save card'), 'error');
    }
  }

  async function archive(t: CardTemplate) {
    if (!window.confirm(`Archive "${t.label}"?`)) return;
    try {
      await archiveTemplate(t.id);
      toast('Card archived', 'info');
    } catch (e) {
      toast(formatError(e, 'Could not archive card'), 'error');
    }
  }

  return (
    <div className="pb-8">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-muted">Long-press a card to edit or archive. Drag to reorder.</p>
        <button
          onClick={() => setEditor({ open: true })}
          className="rounded-xl bg-sage px-4 py-2 text-sm font-medium text-white"
        >
          + New
        </button>
      </div>

      {Object.keys(CATEGORIES).map((cat) => {
        const arr = grouped.get(cat);
        if (!arr || arr.length === 0) return null;
        const c = CATEGORIES[cat];
        return (
          <section key={cat} className="mb-6">
            <h2 className="mb-2 px-1 text-sm font-medium text-text-primary">
              <span aria-hidden>{c.emoji}</span> {c.label}
            </h2>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd(cat)}>
              <SortableContext items={arr.map((t) => t.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-3 gap-2">
                  {arr.map((t) => (
                    <SortableTile key={t.id} template={t} onLongPress={setActionsTarget} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </section>
        );
      })}

      <CardEditor
        open={editor.open}
        initial={editor.initial}
        onClose={() => setEditor({ open: false })}
        onSubmit={submitEditor}
      />
      <CardActionsMenu
        template={actionsTarget}
        canEdit={!!actionsTarget && actionsTarget.created_by === me?.id}
        onClose={() => setActionsTarget(null)}
        onEdit={() => {
          if (actionsTarget) setEditor({ open: true, initial: actionsTarget });
          setActionsTarget(null);
        }}
        onArchive={() => {
          if (actionsTarget) void archive(actionsTarget);
          setActionsTarget(null);
        }}
        onToggleFavorite={() => {
          if (actionsTarget) {
            void toggleFavorite(actionsTarget.id).catch((e) => toast(formatError(e, 'Could not update favourite'), 'error'));
          }
          setActionsTarget(null);
        }}
      />
    </div>
  );
}
